/**
 * Seção "Anexos" do detalhe do ticket (v3): upload multipart, lista com
 * nome/tamanho/autor/data, download autenticado (fetch + blob, padrão da KB)
 * e exclusão com confirmação. Spec 066 (D2): com a seção na tela, Ctrl+V de um
 * print ou de arquivos copiados anexa na hora.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MAX_UPLOAD_BYTES, type TicketFile } from '@erp/shared';
import { Download, Paperclip, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, Spinner } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { ticketFilesQuery } from './tickets-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { downloadTicketFile, useDeleteTicketFile, useUploadTicketFile } from './tickets-mutations';
import { errorMessage } from './ticket-format';
import { pasteBelongsToAttachments, pastedFileName } from './attachment-paste';

const MAX_LABEL = `${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB`;

export function AttachmentsSection({
  ticketNumber,
  readOnly,
}: {
  ticketNumber: number;
  readOnly: boolean;
}) {
  const filesQ = useQuery(ticketFilesQuery(ticketNumber));
  // quem anexou é sempre alguém da equipe (a rota é da equipe): o nome sai da
  // equipe do tenant, inativos inclusive — GET /users é global e cortava em 200
  const usersQ = useQuery(teamListQuery({ includeInactive: true }));
  const upload = useUploadTicketFile(ticketNumber);
  const remove = useDeleteTicketFile();

  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TicketFile | null>(null);

  const userName = (id: number | null): string => {
    if (id === null) return '—';
    return usersQ.data?.items.find((u) => u.id === id)?.name ?? `Usuário #${id}`;
  };

  const onPickFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`O arquivo excede o limite de ${MAX_LABEL}.`);
      return;
    }
    upload.mutate(file, { onError: (e) => setError(errorMessage(e)) });
  };

  // spec 066 (D2): Ctrl+V fora de campo de texto e de janela anexa o que foi
  // copiado, um arquivo por vez. O handler lê o estado pela ref: o efeito liga
  // uma vez por ticket, e não a cada render.
  const pasteRef = useRef<(files: File[]) => void>(() => {});
  pasteRef.current = (pasted: File[]) => {
    if (upload.isPending) return;
    const at = new Date();
    const files = pasted.map((f) => new File([f], pastedFileName(f, at), { type: f.type }));
    const tooBig = files.find((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} excede o limite de ${MAX_LABEL}.`);
      return;
    }
    setError(null);
    void (async () => {
      for (const file of files) {
        try {
          await upload.mutateAsync(file);
        } catch (e) {
          setError(errorMessage(e));
          return;
        }
      }
    })();
  };
  useEffect(() => {
    if (readOnly) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length === 0) return;
      const target = e.target instanceof Element ? e.target : null;
      if (!pasteBelongsToAttachments(target)) return;
      e.preventDefault();
      pasteRef.current(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [readOnly, ticketNumber]);

  const download = (file: TicketFile) => {
    setError(null);
    downloadTicketFile(file).catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Falha no download')
    );
  };

  const files = filesQ.data ?? [];

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          Anexos{files.length > 0 ? ` (${files.length})` : ''}
        </h2>
        {!readOnly ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-tour="anexos-colar">
              Até {MAX_LABEL} por arquivo, ou cole um print (Ctrl+V)
            </span>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                onPickFile(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              {upload.isPending ? <Spinner className="size-4" /> : <Upload />}
              Anexar arquivo
            </Button>
          </div>
        ) : null}
      </div>

      {filesQ.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      ) : filesQ.isError ? (
        <p className="text-sm text-muted-foreground">Erro ao carregar os anexos.</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum anexo neste ticket.</p>
      ) : (
        <ul className="divide-y">
          {files.map((file) => (
            <li key={file.id} className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
              <Paperclip className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={file.fileName}>
                  {file.fileName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)} · {userName(file.uploadedBy)} ·{' '}
                  {formatDateTime(file.createdAt)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-muted-foreground hover:text-primary"
                title={`Baixar ${file.fileName}`}
                onClick={() => download(file)}
              >
                <Download />
              </Button>
              {!readOnly ? (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-muted-foreground hover:text-destructive"
                  title={`Excluir ${file.fileName}`}
                  disabled={remove.isPending}
                  onClick={() => setConfirmDelete(file)}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}

      {confirmDelete ? (
        <Dialog open onClose={() => setConfirmDelete(null)} title="Excluir anexo">
          <p className="text-sm text-muted-foreground">
            Tem certeza que deseja excluir o anexo{' '}
            <span className="font-medium text-foreground">{confirmDelete.fileName}</span>? Esta
            ação não pode ser desfeita.
          </p>
          {remove.isError ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })
              }
            >
              {remove.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
              Excluir anexo
            </Button>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}
